import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthState } from "./auth.state";
import {
  registerSendCode,
  registerVerifyCode,
  loginSendCode,
  loginVerifyCode,
  logoutUser,
  deleteAccount,
  getUserProfile,
} from "./auth.thunk";
import {
  RegisterResponse,
  LoginResponse,
  AuthData,
  UserProfileResponse,
} from "./auth.types";
import { tokenStorage } from "../../api/tokenStorage";

const initialState: AuthState = {
  data: null,
  error: null,
  status: "idle",
  codeSent: false,
  isAuthenticated: false,
  userProfile: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.data = null;
      state.isAuthenticated = false;
      state.codeSent = false;
      state.status = "idle";
      state.error = null;
      state.userProfile = null;
      tokenStorage.clearTokens();
    },
    setAuthData: (state, action: PayloadAction<AuthData>) => {
      state.data = action.payload;
      state.isAuthenticated = true;
      tokenStorage.setTokens(
        action.payload.access_token,
        action.payload.refresh_token,
      );
    },
    resetCodeSent: (state) => {
      state.codeSent = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (build) => {
    build
      .addCase(registerSendCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        registerSendCode.fulfilled,
        (state, action: PayloadAction<RegisterResponse>) => {
          state.status = "success";
          if (
            Array.isArray(action.payload.data) &&
            action.payload.data.length === 0
          ) {
            state.codeSent = true;
          }
        },
      )
      .addCase(
        registerSendCode.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.status = "error";
          state.error = action.payload ?? "Bir hata oluştu.";
        },
      )
      .addCase(registerVerifyCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        registerVerifyCode.fulfilled,
        (state, action: PayloadAction<RegisterResponse>) => {
          state.status = "success";
          if (!Array.isArray(action.payload.data) && action.payload.data) {
            state.data = action.payload.data;
            state.isAuthenticated = true;
            state.codeSent = false;
            tokenStorage.setTokens(
              action.payload.data.access_token,
              action.payload.data.refresh_token,
            );
          }
        },
      )
      .addCase(
        registerVerifyCode.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.status = "error";
          state.error = action.payload ?? "Bir hata oluştu.";
        },
      )
      .addCase(loginSendCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        loginSendCode.fulfilled,
        (state, action: PayloadAction<LoginResponse>) => {
          state.status = "success";
          if (
            Array.isArray(action.payload.data) &&
            action.payload.data.length === 0
          ) {
            state.codeSent = true;
          }
        },
      )
      .addCase(
        loginSendCode.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.status = "error";
          state.error = action.payload ?? "Bir hata oluştu.";
        },
      )
      .addCase(loginVerifyCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        loginVerifyCode.fulfilled,
        (state, action: PayloadAction<LoginResponse>) => {
          state.status = "success";
          if (!Array.isArray(action.payload.data) && action.payload.data) {
            state.data = action.payload.data;
            state.isAuthenticated = true;
            state.codeSent = false;
            tokenStorage.setTokens(
              action.payload.data.access_token,
              action.payload.data.refresh_token,
            );
          }
        },
      )
      .addCase(
        loginVerifyCode.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.status = "error";
          state.error = action.payload ?? "Bir hata oluştu.";
        },
      )
      .addCase(logoutUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.data = null;
        state.isAuthenticated = false;
        state.codeSent = false;
        state.status = "idle";
        state.error = null;
        state.userProfile = null;
        tokenStorage.clearTokens();
      })
      .addCase(
        logoutUser.rejected,
        (state) => {
          // API hatası olsa bile local oturum tamamen kapatılır
          state.data = null;
          state.isAuthenticated = false;
          state.codeSent = false;
          state.status = "idle";
          state.error = null;
          state.userProfile = null;
          tokenStorage.clearTokens();
        },
      )
      .addCase(deleteAccount.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.data = null;
        state.isAuthenticated = false;
        state.codeSent = false;
        state.status = "idle";
        state.error = null;
        state.userProfile = null;
        tokenStorage.clearTokens();
      })
      .addCase(
        deleteAccount.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.status = "error";
          state.error = action.payload ?? "Bir hata oluştu.";
        },
      )
      .addCase(getUserProfile.pending, (state) => {
        // Kullanıcı zaten authenticated ise status'u "loading"'e çekme —
        // login sonrası background profile fetch, login flow UI'ını donduruyor
        if (!state.isAuthenticated) {
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(
        getUserProfile.fulfilled,
        (state, action: PayloadAction<UserProfileResponse>) => {
          state.status = "success";
          state.userProfile = action.payload.data;
          state.isAuthenticated = true;
          // Sync customer fields from the authoritative profile response
          // so state.data.customer and state.userProfile stay consistent
          if (state.data) {
            const p = action.payload.data;
            state.data.customer = {
              ...state.data.customer,
              id: p.id,
              firstname: p.firstname,
              lastname: p.lastname,
              email: p.email,
              gender: p.gender ?? state.data.customer.gender,
              phone_number: p.phone,
              profile_photo_path: p.photo,
            };
          }
        },
      )
      .addCase(
        getUserProfile.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.status = "error";
          state.error = action.payload ?? "Bir hata oluştu.";
          // Profil çekme hatası oturumu sonlandırmamalı — token cookie'de geçerliyse
          // isAuthenticated ve token'lar korunur. 401 durumunu axiosInstance interceptor'ı yönetir.
        },
      );
  },
});

export const { logout, setAuthData, resetCodeSent, clearError } =
  authSlice.actions;
export default authSlice.reducer;
